/**
 * @fileoverview Pure adapters for the Teacher Assessment Drawer payload.
 *
 * Maps `GET /api/arc_mosaico/session_meetings/:id/list_assessment_scores`
 * (after humps camelization) into the drawer items and students. Kept free of
 * runtime imports so it can be unit-tested without the API client.
 */

import type {
  AssessmentItem,
  AssessmentScore,
  CurriculumKind,
  DrawerStudent,
  PerStudentAssessment,
} from './types'

// Raw types after humps camelization. Names mirror the AR-538 docs.
export interface RawAssessmentScore {
  id: string
  assessmentId: string
  masterScore: number
  studentScore: number
  justification: string | null
  doNotAssess: boolean
  pendingTutorAssessment: boolean
  studentAssessed: boolean
}

export interface RawStudent {
  id: string
  fullName: string
  firstName: string
  lastName: string
  avatarUrl: string
  assessmentScore: RawAssessmentScore
}

export interface RawItem {
  lv4ItemId: string
  name: string
  color: string
  breadcrumb: string
  curriculumKind: CurriculumKind
  lv1Name: string
  originalCodes: string
  /** Rubric text keyed by score "1".."5" (AR-828). */
  rubrics?: Record<string, string>
  pendingTutorAssessment: boolean
  students: RawStudent[]
}

export interface RawActivity {
  id: string
  title: string
  /** Absent on backends that predate AR-944; treated as assessable then. */
  hasAssessments?: boolean
  hasDeliverable: boolean
  standards: RawItem[]
  skills: RawItem[]
}

export interface ListAssessmentScoresResponse {
  project: { id: string; name: string }
  sessionMeeting: { id: string; title: string }
  activities: RawActivity[]
}

export function adaptScore(raw: RawAssessmentScore): AssessmentScore {
  return {
    id: raw.id,
    assessmentId: raw.assessmentId,
    masterScore: raw.masterScore,
    studentScore: raw.studentScore,
    justification: raw.justification,
    doNotAssess: raw.doNotAssess,
    pendingTutorAssessment: raw.pendingTutorAssessment,
    studentAssessed: raw.studentAssessed,
  }
}

export function adaptStudent(raw: RawStudent): DrawerStudent {
  return {
    id: raw.id,
    firstName: raw.firstName,
    lastName: raw.lastName,
    fullName: raw.fullName,
    avatarUrl: raw.avatarUrl ?? '',
  }
}

/**
 * The backend returns every activity of the lesson, including the ones with
 * "Enable assessment" off. Their items are not assessable, and since scores are
 * grouped by `lv4_item_id` an item shared with an assessable activity would
 * otherwise show up under the wrong activity.
 */
function isAssessable(activity: RawActivity): boolean {
  return activity.hasAssessments !== false
}

function hasStudents(raw: RawItem): boolean {
  return Boolean(raw.students && raw.students.length > 0)
}

/**
 * Flattens activities[].{standards,skills}[] into a single list per kind,
 * keeping only items of assessable activities that have score rows, and
 * deduplicated by `lv4_item_id`: the same `lv4_item` is returned once per
 * activity that binds it, with the same `assessment_score.id`, so the first
 * occurrence wins and `activityHasDeliverable = true` propagates from any
 * sibling activity with a deliverable.
 */
export function buildItems(
  activities: RawActivity[],
  kind: 'standards' | 'skills',
): AssessmentItem[] {
  const byLv4Item = new Map<string, AssessmentItem>()

  for (const activity of activities.filter(isAssessable)) {
    for (const raw of activity[kind]) {
      // No score rows yet: nothing the teacher can assess (see countUngeneratedItems).
      if (!hasStudents(raw)) continue

      const existing = byLv4Item.get(raw.lv4ItemId)
      if (existing) {
        // Propagate deliverable flag across duplicates (immutable update).
        if (activity.hasDeliverable && !existing.activityHasDeliverable) {
          byLv4Item.set(raw.lv4ItemId, {
            ...existing,
            activityHasDeliverable: true,
            activityId: activity.id,
            activityTitle: activity.title,
          })
        }
        continue
      }

      const perStudent: Record<string, PerStudentAssessment> = {}
      for (const s of raw.students) {
        perStudent[s.id] = { score: adaptScore(s.assessmentScore) }
      }
      const enabled = !raw.students.every(s => s.assessmentScore.doNotAssess)

      byLv4Item.set(raw.lv4ItemId, {
        lv4ItemId: raw.lv4ItemId,
        curriculumKind: raw.curriculumKind,
        name: raw.name,
        color: raw.color,
        breadcrumb: raw.breadcrumb,
        lv1Name: raw.lv1Name,
        originalCodes: raw.originalCodes,
        rubrics: raw.rubrics ?? {},
        pendingTutorAssessment: raw.pendingTutorAssessment,
        activityId: activity.id,
        activityTitle: activity.title,
        activityHasDeliverable: activity.hasDeliverable,
        enabled,
        perStudent,
      })
    }
  }
  return Array.from(byLv4Item.values())
}

/**
 * Distinct curriculum items of assessable activities that came back without
 * score rows. The drawer uses it to tell "this lesson has no curriculum items"
 * apart from "the assessments of this lesson are not ready yet".
 */
export function countUngeneratedItems(activities: RawActivity[]): number {
  const assessable = activities.filter(isAssessable)
  const ready = new Set<string>()
  const ungenerated = new Set<string>()

  for (const activity of assessable) {
    for (const raw of [...activity.standards, ...activity.skills]) {
      if (hasStudents(raw)) ready.add(raw.lv4ItemId)
      else ungenerated.add(raw.lv4ItemId)
    }
  }
  return Array.from(ungenerated).filter(id => !ready.has(id)).length
}

/** Collects unique students across every assessable item in the lesson. */
export function buildStudents(activities: RawActivity[]): DrawerStudent[] {
  const map = new Map<string, DrawerStudent>()
  for (const activity of activities.filter(isAssessable)) {
    for (const item of [...activity.standards, ...activity.skills]) {
      for (const s of item.students) {
        if (!map.has(s.id)) map.set(s.id, adaptStudent(s))
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => a.fullName.localeCompare(b.fullName))
}
